import React from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import Titulo from '../../components/tipografia/Titulo';
import Descricao from '../../components/tipografia/Descricao';
import VBox from '../../components/layout/VBox';
import HBox from '../../components/layout/HBox';
import Grid, { GridItem } from '../../components/layout/Grid';
import Icon from '@/components/Icon';
import { mdiPackageVariant, mdiCheckCircle, mdiClockOutline, mdiAlertCircle, mdiClose, mdiRocketLaunch } from '@mdi/js';
import styles from './page.module.css';
import Botao from '@/components/Botao';

type PlanoItem = {
    id: number;
    identificador: string;
    capacidade: number;
    valor_unitario: number;
    valor_excedente: number;
    data_exclusao?: Date | null;
};

type Plano = {
    id: number;
    nome: string;
    descricao: string;
    valor_setup?: number;
    parcelas_setup?: number;
    itens: PlanoItem[];
};

type OrgInscricao = {
    id: number;
    organizacao_id: number;
    plano_id: number;
    status: 'aguardando_contrato' | 'aguardando_pagamento' | 'ativa' | 'cancelada';
    data_inicio?: string;
    data_fim?: string;
    valor_mensal?: number;
    valor_setup?: number;
    parcelas_setup?: number;
    boleto_id?: string;
    contrato_envelope_id?: string;
    eventos?: Array<{
        tipo: string;
        data: string;
        usuario_id?: number;
        detalhes?: Record<string, unknown>;
    }>;
    data_criacao: string;
    data_atualizacao: string;
};

type PlanoResponse = {
    ok: boolean;
    result: {
        plano: Plano;
        status_inscricao?: string;
        inscricao_ativa?: OrgInscricao;
        inscricao_em_processo?: OrgInscricao;
    };
};

const moneyFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
});

// Mapa de identificadores para nomes amigáveis
const identificadorParaNome: Record<string, [string, string]> = {
    'licenca': ['Licença de Uso', 'Licenças de Uso'],
    'usuarios': ['Usuário', 'Usuários'],
    'usuarios_online': ['Usuário Online Simultâneo', 'Usuários Online Simultâneos'],
    'canais': ['Canal de Atendimento', 'Canais de Atendimento'],
    'cda': ['Conversa Distinta Ativa (CDA)', 'Conversas Distintas Ativas (CDA)'],
    'conversas_ia': ['Conversa com IA', 'Conversas com IA'],
    'notificacoes': ['Notificação', 'Notificações'],
};

const StatusInscricaoBadge = ({ status }: { status?: string }) => {
    if (!status) return null;

    const statusConfig: Record<string, { label: string; icon: string; color: string }> = {
        'aguardando_contrato': {
            label: 'Aguardando assinatura do contrato',
            icon: mdiClockOutline,
            color: 'var(--tc-color-warning)'
        },
        'aguardando_pagamento': {
            label: 'Aguardando Pagamento',
            icon: mdiClockOutline,
            color: 'var(--tc-color-info)'
        },
        'ativo': {
            label: 'Inscrição Ativa',
            icon: mdiCheckCircle,
            color: 'var(--tc-color-success)'
        },
        'cancelada': {
            label: 'Inscrição Cancelada',
            icon: mdiClose,
            color: 'var(--tc-color-danger)'
        },
    };

    const config = statusConfig[status] || {
        label: status,
        icon: mdiAlertCircle,
        color: '#6b7280'
    };

    return (
        <div className={styles.badge} style={{ borderColor: config.color, borderWidth: '1px', borderStyle: 'solid', color: config.color }}>
            <Icon path={config.icon} size={0.7} />
            <span>{config.label}</span>
        </div>
    );
};

const getItemDescricao = (item: PlanoItem): string => {
    const capacidade = +item.capacidade;
    const valorUnitario = +item.valor_unitario;
    const valorExcedente = +item.valor_excedente;

    const identificador = identificadorParaNome[item.identificador]

    if (!identificador) {
        return `Item desconhecido ${item.identificador})`;
    }

    if (capacidade === 0 && valorUnitario === 0 && valorExcedente === 0) {
        return `${identificador[1]} sem limite de uso`;
    }

    // Capacidade zerada = valor varia conforme uso
    if (capacidade === 0) {
        return `${moneyFormatter.format(valorUnitario)} / ${identificador[1]} / mês`;
    }

    // Valor zerado = capacidade fixa
    if (valorUnitario === 0 && valorExcedente === 0) {
        return `${capacidade} ${capacidade === 1 ? identificador[0] : identificador[1]}`;
    }

    // Tem valor excedente
    if (valorExcedente > 0) {
        return `${capacidade} ${capacidade === 1 ? identificador[0] : identificador[1]} (${moneyFormatter.format(valorExcedente)} / ${identificador[0]} excedente)`;
    }

    // Capacidade fixa com valor
    return `${capacidade} ${capacidade === 1 ? identificador[0] : identificador[1]} - ${moneyFormatter.format(valorUnitario)}`;
};

export const ConteudoPlanoAtual = ({ compact = false }: { compact?: boolean }) => {
    const { data, isLoading, error } = useQuery<PlanoResponse>(
        'plano-atual',
        async () => {
            const response = await axios.get('/api/organizacao/plano', {
                withCredentials: true,
            });
            return response.data;
        }
    );

    if (isLoading) {
        return (
            <VBox gap="16px">
                <p>Carregando plano...</p>
            </VBox>
        );
    }

    if (error || !data?.ok) {
        return (
            <VBox gap="16px">
                <Titulo>Erro</Titulo>
                <p>Não foi possível carregar o plano atual.</p>
            </VBox>
        );
    }

    const { plano, status_inscricao, inscricao_em_processo } = data.result;

    return (
        <VBox gap="24px" style={compact ? {} : { padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            {/* Header */}
            <HBox gap="16px" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <VBox gap="8px">
                    <HBox gap="12px" style={{ alignItems: 'center' }}>
                        <Icon path={mdiPackageVariant} size={1.5} color="var(--tc-color-primary)" />
                        <Titulo>Plano Atual</Titulo>
                    </HBox>
                    <Descricao>{plano.descricao}</Descricao>
                </VBox>
                <StatusInscricaoBadge status={inscricao_em_processo?.status || status_inscricao} />
            </HBox>

            {/* Nome do Plano */}
            <div className={styles.planoCard}>
                <HBox stretch>
                    <h2 className={styles.planoNome} style={{ textAlign: 'center' }}>{plano.nome}</h2>
                    <Botao variant="primary" icon={mdiRocketLaunch} href="/upgrade-plano">Upgrade</Botao>
                </HBox>

                {plano.valor_setup && plano.valor_setup > 0 && (
                    <div className={styles.setupInfo} style={{ textAlign: 'center' }}>
                        <p>
                            <strong>Valor de Setup:</strong> {moneyFormatter.format(plano.valor_setup)}
                            {plano.parcelas_setup && plano.parcelas_setup > 1 && (
                                <span> em {plano.parcelas_setup}x</span>
                            )}
                        </p>
                    </div>
                )}
                {/* Itens do Plano */}
                {plano.itens && plano.itens.length > 0 && (
                    <Grid>
                        <GridItem sm={12} md={4}>&nbsp;</GridItem>
                        <GridItem sm={12} md={4}>
                            <div className={styles.tabelaContainer}>
                                <table className={styles.tabela}>
                                    <tbody>
                                        {plano.itens
                                            .filter(item => !item.data_exclusao)
                                            .map((item) => {
                                                const descricao = getItemDescricao(item);
                                                return (
                                                    <tr key={item.id}>
                                                        <td className={styles.tabelaDescricao}>{descricao}</td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </div>
                        </GridItem>
                        <GridItem sm={12} md={4}>&nbsp;</GridItem>
                    </Grid>
                )}
            </div>


            {/* Informações adicionais */}
            {inscricao_em_processo?.status === 'aguardando_contrato' && (
                <div className={styles.alertInfo}>
                    <Icon path={mdiClockOutline} size={0.8} />
                    <p>Sua inscrição está sendo processada. Aguarde o contato de nossa equipe para assinar o contrato.</p>
                </div>
            )}

            {inscricao_em_processo?.status === 'aguardando_pagamento' && (
                <div className={styles.alertInfo}>
                    <Icon path={mdiClockOutline} size={0.8} />
                    <p>Aguardando confirmação do pagamento da inscrição.</p>
                </div>
            )}
        </VBox>
    );
};

export default ConteudoPlanoAtual;
